import { toStorageProxyUrl } from './storageProxy';

const PAGE_MARGIN_PT = 28;
const PIXEL_RATIO = 2;
/** A cut may move up by at most this share of a page to land between lines. */
const MAX_BREAK_SHIFT = 0.35;
const ASSET_TIMEOUT_MS = 15_000;
const BLANK_PIXEL =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

/**
 * An image that shows on screen but could not be copied into the PDF — almost always a
 * storage host without CORS headers (the `<img>` loads, but `fetch()` of its bytes is blocked).
 */
export class PdfImageEmbedError extends Error {
  constructor(readonly urls: string[]) {
    super(
      `${String(urls.length)} image${urls.length === 1 ? '' : 's'} (e.g. a signature) could not be added to the PDF. ` +
        'The file storage must allow cross-origin downloads.',
    );
    this.name = 'PdfImageEmbedError';
  }
}

/**
 * Rows (CSS px from the element top) covered by a text line or a replaced element.
 * A page cut is only safe on an uncovered row — otherwise a line would be sliced in two.
 */
function collectCoveredRows(root: HTMLElement): Uint8Array {
  const origin = root.getBoundingClientRect().top;
  const height = Math.ceil(root.scrollHeight);
  const diff = new Int32Array(height + 2);

  const cover = (rect: DOMRect) => {
    if (rect.height <= 0) return;
    const top = Math.max(0, Math.floor(rect.top - origin));
    const bottom = Math.min(height, Math.ceil(rect.bottom - origin));
    if (bottom - top <= 1) return;
    diff[top + 1] = (diff[top + 1] ?? 0) + 1;
    diff[bottom] = (diff[bottom] ?? 0) - 1;
  };

  const range = document.createRange();
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (!node.textContent?.trim()) continue;
    range.selectNodeContents(node);
    for (const rect of Array.from(range.getClientRects())) cover(rect);
  }
  root.querySelectorAll('img, svg, canvas, input, textarea, select, video').forEach((el) => {
    cover(el.getBoundingClientRect());
  });

  const covered = new Uint8Array(height + 1);
  let running = 0;
  for (let row = 0; row <= height; row += 1) {
    running += diff[row] ?? 0;
    covered[row] = running > 0 ? 1 : 0;
  }
  return covered;
}

function pageBreaks(covered: Uint8Array, totalHeight: number, pageHeight: number): number[] {
  const breaks = [0];
  let start = 0;
  while (start + pageHeight < totalHeight) {
    const ideal = Math.floor(start + pageHeight);
    const floor = Math.floor(start + pageHeight * (1 - MAX_BREAK_SHIFT));
    let cut = ideal;
    while (cut > floor && covered[cut] === 1) cut -= 1;
    if (cut <= floor) cut = ideal;
    breaks.push(cut);
    start = cut;
  }
  breaks.push(totalHeight);
  return breaks;
}

async function waitForAssets(root: HTMLElement): Promise<void> {
  await document.fonts.ready;
  await Promise.all(
    Array.from(root.querySelectorAll('img')).map((img) =>
      img.complete ? Promise.resolve() : img.decode().catch(() => undefined),
    ),
  );
  // rAF never fires in a background tab, so a timeout caps the wait for the next paint.
  await new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, 150);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        clearTimeout(timer);
        resolve();
      });
    });
  });
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') resolve(reader.result);
      else reject(new Error('Could not read image'));
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error('Could not read image'));
    };
    reader.readAsDataURL(blob);
  });
}

/** Storage images come through the same-origin proxy; anything else uses the normal fetch. */
async function fetchViaStorageProxy(url: string): Promise<string | false> {
  const proxied = toStorageProxyUrl(url);
  if (!proxied) return false;
  const res = await fetch(proxied, { credentials: 'omit', cache: 'no-cache' });
  if (!res.ok) return false;
  return blobToDataUrl(await res.blob());
}

/** Renders `root` to a paginated A4 PDF. `root` must be laid out (mounted, not `display:none`). */
export async function elementToPdf(root: HTMLElement, background = '#FFFFFF'): Promise<Blob> {
  const [{ domToCanvas }, { jsPDF }] = await Promise.all([
    import('modern-screenshot'),
    import('jspdf'),
  ]);

  await waitForAssets(root);

  const cssWidth = root.scrollWidth;
  const cssHeight = root.scrollHeight;
  const covered = collectCoveredRows(root);
  const visibleImages = new Set(
    Array.from(root.querySelectorAll('img'))
      .filter((img) => img.complete && img.naturalWidth > 0)
      .map((img) => img.currentSrc || img.src),
  );
  const unembedded = new Set<string>();

  const canvas = await domToCanvas(root, {
    scale: PIXEL_RATIO,
    backgroundColor: background,
    width: cssWidth,
    height: cssHeight,
    timeout: ASSET_TIMEOUT_MS,
    fetchFn: fetchViaStorageProxy,
    fetch: {
      // Revalidate so a cached no-CORS response from the on-screen `<img>` is not reused.
      requestInit: { cache: 'no-cache' },
      placeholderImage: (cloned) => {
        const src = cloned instanceof HTMLImageElement ? cloned.currentSrc || cloned.src : '';
        if (visibleImages.has(src)) unembedded.add(src);
        return BLANK_PIXEL;
      },
    },
  });

  // Blank is fine for an upload that is broken on screen too; losing a visible signature is not.
  if (unembedded.size > 0) throw new PdfImageEmbedError([...unembedded]);

  const pdf = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'portrait', compress: true });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const contentWidth = pageWidth - PAGE_MARGIN_PT * 2;
  const ptPerCssPx = contentWidth / cssWidth;
  const pageCssHeight = (pageHeight - PAGE_MARGIN_PT * 2) / ptPerCssPx;

  const breaks = pageBreaks(covered, cssHeight, pageCssHeight);
  const slice = document.createElement('canvas');
  const ctx = slice.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');

  for (let i = 0; i < breaks.length - 1; i += 1) {
    const top = breaks[i] ?? 0;
    const sliceCssHeight = (breaks[i + 1] ?? cssHeight) - top;
    if (sliceCssHeight <= 0) continue;

    slice.width = canvas.width;
    slice.height = Math.ceil(sliceCssHeight * PIXEL_RATIO);
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, slice.width, slice.height);
    ctx.drawImage(
      canvas,
      0,
      Math.floor(top * PIXEL_RATIO),
      canvas.width,
      slice.height,
      0,
      0,
      slice.width,
      slice.height,
    );

    if (i > 0) pdf.addPage();
    pdf.addImage(
      slice.toDataURL('image/jpeg', 0.92),
      'JPEG',
      PAGE_MARGIN_PT,
      PAGE_MARGIN_PT,
      contentWidth,
      sliceCssHeight * ptPerCssPx,
      undefined,
      'FAST',
    );
  }

  return pdf.output('blob');
}
